/**
 * Meta Pixel utility for Lipolaser Los Angeles LLC — Lipo Laser Los Angeles
 * Pixel ID: 1575828340726848
 *
 * LANDING ARCHETYPE: Pure lead generation (no e-commerce, no checkout).
 * Optimized for Meta "Website Lead" campaign objective.
 *
 * Lead-generation funnel (clean, no noise):
 *   1. PageView          → auto on load                     (audiences + lookalikes)
 *   2. ViewContent       → user engages with the offer       (warm audience)
 *   3. InitiateCheckout  → reaches the consultation form     (high-intent retargeting)
 *   4. Lead              → submits the GHL form             (THE conversion)
 *   5. SubmitApplication → opens Cherry financing           (secondary conversion)
 *   6. Contact           → taps the clinic phone number     (offline intent)
 *
 * Configure Lead as your top Priority Event in Aggregated Event Measurement.
 */

import { getIntentEventParams } from './trafficIntent'

export const META_PIXEL_ID = '1575828340726848'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
  }
}

let initialized = false

// UUID v4 for event deduplication (used when you later add Conversions API server-side)
export function generateEventId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as any).randomUUID === 'function'
  ) {
    return (crypto as any).randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function initMetaPixel() {
  if (typeof window === 'undefined') return
  if (initialized) return
  if (window.fbq) {
    initialized = true
    return
  }

  // Meta's official snippet inserts fbevents.js *before the first existing
  // <script> tag*. In a bundled SPA the document can legitimately contain no
  // <script> element at that moment, so that lookup returns undefined and the
  // snippet throws on `.parentNode`. We keep the exact same command queue
  // behaviour but append the tag to <head> instead, which always exists.
  const queue: any = function (...args: any[]) {
    queue.callMethod
      ? queue.callMethod.apply(queue, args)
      : queue.queue.push(args)
  }
  queue.push = queue
  queue.loaded = true
  queue.version = '2.0'
  queue.queue = []

  window.fbq = queue
  if (!window._fbq) window._fbq = queue

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const mount = document.head || document.body || document.documentElement
  if (mount) mount.appendChild(script)

  window.fbq('init', META_PIXEL_ID)
  window.fbq('track', 'PageView')
  initialized = true
}

function trackEvent(
  eventName: string,
  params?: Record<string, any>,
  options?: { eventID?: string },
) {
  if (typeof window === 'undefined' || !window.fbq) return
  const fullParams = {
    ...getIntentEventParams(),
    ...(params || {}),
    event_source_url:
      typeof window !== 'undefined' ? window.location.href : undefined,
  }
  if (options?.eventID) {
    window.fbq('track', eventName, fullParams, { eventID: options.eventID })
  } else {
    window.fbq('track', eventName, fullParams)
  }
}

/**
 * Fired when the visitor meaningfully engages with the offer (scrolls into the
 * benefits/treatment story). Builds the warm audience for retargeting.
 */
export function trackViewContent(params?: Record<string, any>) {
  trackEvent('ViewContent', {
    content_name: 'Lipolaser Offer Viewed',
    content_category: 'Body Contouring',
    ...params,
  })
}

/**
 * Fired when the user reaches the consultation form section.
 * Best signal for retargeting "interested but didn't convert" audiences.
 */
export function trackInitiateCheckout(params?: Record<string, any>) {
  trackEvent('InitiateCheckout', {
    content_name: 'Reached Consultation Form',
    content_category: 'Body Contouring',
    currency: 'USD',
    value: 250,
    ...params,
  })
}

/**
 * THE conversion event. Fires only on real GHL form submission.
 * Set as Priority Event #1 in Aggregated Event Measurement.
 */
export function trackLead(params?: Record<string, any>) {
  const eventID = generateEventId()
  trackEvent(
    'Lead',
    {
      content_name: 'Lipolaser Consultation Request',
      content_category: 'Body Contouring',
      currency: 'USD',
      value: 250,
      ...params,
    },
    { eventID },
  )
  if (typeof console !== 'undefined') {
    console.log('[Meta Pixel] Lead fired', { eventID, params })
  }
}

/**
 * Secondary conversion: the patient opens the Cherry financing application.
 * Strong purchase-intent signal for lookalike building.
 */
export function trackSubmitApplication(params?: Record<string, any>) {
  const eventID = generateEventId()
  trackEvent(
    'SubmitApplication',
    {
      content_name: 'Cherry Financing Application Opened',
      content_category: 'Financing',
      currency: 'USD',
      value: 250,
      ...params,
    },
    { eventID },
  )
}

/**
 * Fired when the visitor taps the clinic phone number — offline lead intent.
 */
export function trackContact(params?: Record<string, any>) {
  trackEvent('Contact', {
    content_name: 'Clinic Phone Tap',
    content_category: 'Body Contouring',
    ...params,
  })
}

/**
 * Fired when a booking CTA is clicked anywhere on the landing page.
 * Custom event — useful for CTA-level optimization and funnel diagnostics.
 */
export function trackBookingCtaClick(location: string) {
  trackEvent('BookingCtaClick', {
    content_name: 'Booking CTA Clicked',
    cta_location: location,
  })
}

/**
 * Custom event per qualification wizard step. Lets you see exactly where
 * visitors drop off (e.g. the credit question) and optimize copy accordingly.
 * NOT a conversion — never set as a Priority Event.
 */
export function trackWizardStep(
  stepIndex: number,
  stepName: string,
  answer?: string,
) {
  trackEvent('QualifyStep', {
    content_name: `Wizard Step ${stepIndex + 1} — ${stepName}`,
    step_index: stepIndex + 1,
    step_name: stepName,
    step_answer: answer,
  })
}

/**
 * Fired when the visitor finishes all qualification steps and the contact
 * form is revealed. High-intent retargeting audience — these people are one
 * step from becoming a Lead.
 */
export function trackWizardComplete(answers: Record<string, unknown>) {
  trackEvent('QualifyComplete', {
    content_name: 'Qualification Completed',
    content_category: 'Body Contouring',
    ...answers,
  })
}
